import { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Text } from '@hotjar/hotsauce';
import { useNotify } from '@hotjar/webapp/hooks/useNotify';
import { useAuthorization } from '@hotjar/webapp/domains/authorization';
import { sessionsSettingsApi } from '@hotjar/webapp/features/Settings/api';
import {
  CheckIcon,
  Container,
  Subtitle,
  TrackingButton,
  StyledLink,
  EnabledSubtitle,
} from './styles';

type Props = {
  siteId?: number;
  hasEnabledCapture: boolean;
  onEnableCapture: () => void;
};

export const NotCapturingErrors = ({
  siteId,
  hasEnabledCapture,
  onEnableCapture,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const { hasPermission } = useAuthorization();
  const { success, error } = useNotify();

  const siteSettingsUrl = `/site/${siteId}/settings#targeting`;
  const hasSessionSettingsUpdatePermission = hasPermission(
    'site',
    `${siteId}`,
    'session-settings-update',
  );

  // @TODO Replace it with packages/recordings/src/hooks/useEnableSessionCapture.ts
  const onSubmit = useCallback(async () => {
    const response = await sessionsSettingsApi.post(siteId as number, 'update_session_settings', {
      session_capture_console_consent: true,
    });

    if (response.success) {
      success(t('common:player.console-tracking-enabled-success'));
      onEnableCapture();
      return;
    }

    error(t('common:player.console-tracking-enabled-error'));
  }, [siteId, error, t, success, onEnableCapture]);

  if (hasEnabledCapture) {
    return (
      <Container>
        <CheckIcon size="l" icon="check" color="text_success" />

        <Text textType="subtitle1">{t('common:player.console-tracking-enabled-title')}</Text>
        <EnabledSubtitle textType="body2">
          <Trans
            i18nKey="common:player.console-tracking-enabled-subtitle"
            components={[
              // eslint-disable-next-line react/jsx-key
              <StyledLink kind="inline" isExternal href={siteSettingsUrl}>
                site settings
              </StyledLink>,
            ]}
          />
        </EnabledSubtitle>
      </Container>
    );
  }

  return (
    <Container>
      <Text textType="subtitle1">{t('common:player.console-start-tracking-title')}</Text>
      <Subtitle textType="body2">{t('common:player.console-start-tracking-subtitle')}</Subtitle>

      {hasSessionSettingsUpdatePermission ? (
        <TrackingButton icon="code_tracking" onClick={onSubmit}>
          {t('common:player.console-start-tracking-button')}
        </TrackingButton>
      ) : (
        <Subtitle textType="body2">{t('common:player.console-only-admins-allowed')}</Subtitle>
      )}
    </Container>
  );
};
